import * as React from 'react';
import { cn } from '../../utils';

export interface MentionOption {
  value: string;
  label: string;
}

export interface MentionTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  /** Current text value (plain text with @mentions inline). */
  value: string;
  /** Called when the text changes. */
  onChange: (value: string) => void;
  /** Options shown when the user types the trigger character. `@feedback` is always prepended automatically. */
  mentions: MentionOption[];
  /** Character that opens the mention popup. @default "@" */
  trigger?: string;
}

const FEEDBACK_OPTION: MentionOption = { value: 'feedback', label: 'feedback' };

/**
 * A textarea that shows a dropdown when the user types `@`, letting them
 * pick from a list of mentions. Selected mentions render with a blue
 * highlight via an overlay behind the textarea.
 */
const MentionTextarea = React.forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  ({ className, value, onChange, mentions, trigger = '@', disabled, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const backdropRef = React.useRef<HTMLDivElement | null>(null);
    const popoverRef = React.useRef<HTMLDivElement | null>(null);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [menuPos, setMenuPos] = React.useState({ top: 0, left: 0 });
    const [query, setQuery] = React.useState('');
    const [cursorStart, setCursorStart] = React.useState<number | null>(null);
    const [highlightedIndex, setHighlightedIndex] = React.useState(0);

    // Build the full option list: @feedback always first, then provided mentions
    const allMentions = React.useMemo(() => {
      const filtered = mentions.filter((m) => m.value !== 'feedback');
      return [FEEDBACK_OPTION, ...filtered];
    }, [mentions]);

    const filtered = React.useMemo(() => {
      if (!query) return allMentions;
      const q = query.toLowerCase();
      return allMentions.filter((m) => m.label.toLowerCase().includes(q));
    }, [allMentions, query]);

    // Reset highlight when filtered list changes
    React.useEffect(() => {
      setHighlightedIndex(0);
    }, [filtered.length]);

    // Sync textarea scroll to backdrop
    const syncScroll = React.useCallback(() => {
      if (innerRef.current && backdropRef.current) {
        backdropRef.current.scrollTop = innerRef.current.scrollTop;
        backdropRef.current.scrollLeft = innerRef.current.scrollLeft;
      }
    }, []);

    // --- helpers ---

    const getCaretCoords = React.useCallback(() => {
      const ta = innerRef.current;
      if (!ta) return { top: 0, left: 0 };
      return measureCaretPosition(ta);
    }, []);

    const closeMention = React.useCallback(() => {
      setMenuOpen(false);
      setQuery('');
      setCursorStart(null);
    }, []);

    const insertMention = React.useCallback(
      (option: MentionOption) => {
        const ta = innerRef.current;
        if (!ta || cursorStart == null) return;

        const before = value.substring(0, cursorStart);
        const after = value.substring(ta.selectionStart ?? cursorStart);
        const mentionText = `${trigger}${option.label} `;
        const newValue = before + mentionText + after;
        onChange(newValue);
        closeMention();

        // Restore focus & cursor after React re-render
        requestAnimationFrame(() => {
          const pos = before.length + mentionText.length;
          ta.focus();
          ta.setSelectionRange(pos, pos);
        });
      },
      [value, onChange, cursorStart, trigger, closeMention],
    );

    // --- event handlers ---

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        onChange(newValue);

        if (!menuOpen || cursorStart == null) return;

        const pos = e.target.selectionStart ?? 0;
        const textSinceTrigger = newValue.substring(cursorStart + trigger.length, pos);
        if (textSinceTrigger.includes(' ') || textSinceTrigger.includes('\n')) {
          closeMention();
        } else {
          setQuery(textSinceTrigger);
        }
      },
      [onChange, menuOpen, cursorStart, trigger, closeMention],
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Detect trigger character
        if (e.key === trigger && !menuOpen) {
          const pos = (e.currentTarget.selectionStart ?? 0);
          // Only trigger at start of input or after whitespace/newline
          const charBefore = pos > 0 ? value[pos - 1] : undefined;
          if (pos === 0 || charBefore === ' ' || charBefore === '\n') {
            setCursorStart(pos);
            setQuery('');
            setMenuOpen(true);

            requestAnimationFrame(() => {
              const coords = getCaretCoords();
              setMenuPos({ top: coords.top + 24, left: coords.left });
            });
          }
          return;
        }

        if (!menuOpen) return;

        if (e.key === 'Escape') {
          e.preventDefault();
          closeMention();
          return;
        }

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((i) => (i + 1) % filtered.length);
          return;
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((i) => (i - 1 + filtered.length) % filtered.length);
          return;
        }

        if (e.key === 'Enter' || e.key === 'Tab') {
          if (filtered.length > 0) {
            e.preventDefault();
            insertMention(filtered[highlightedIndex] ?? filtered[0]!);
          }
          return;
        }

        if (e.key === 'Backspace' && cursorStart != null) {
          const pos = e.currentTarget.selectionStart ?? 0;
          if (pos <= cursorStart) {
            closeMention();
          }
        }
      },
      [menuOpen, value, trigger, getCaretCoords, closeMention, filtered, highlightedIndex, insertMention, cursorStart],
    );

    // Close menu on outside click
    React.useEffect(() => {
      if (!menuOpen) return;
      const handler = (e: MouseEvent) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
          closeMention();
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen, closeMention]);

    // --- highlight rendering ---

    const highlightedHtml = React.useMemo(() => {
      // Match @word (letters, spaces inside a mention are tricky — match @word sequences)
      // We'll match @followed-by-non-whitespace-or-known-mentions
      const mentionLabels = allMentions.map((m) => m.label).sort((a, b) => b.length - a.length);
      const escaped = mentionLabels.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      // Build a pattern that matches @label (case insensitive)
      const pattern = escaped.length > 0
        ? new RegExp(`(${trigger.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:${escaped.join('|')}))(?=\\s|$)`, 'gi')
        : null;

      if (!pattern) return escapeHtml(value);

      let result = '';
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = pattern.exec(value)) !== null) {
        result += escapeHtml(value.substring(lastIndex, match.index));
        result += `<mark class="mention-highlight">${escapeHtml(match[0])}</mark>`;
        lastIndex = match.index + match[0].length;
      }
      result += escapeHtml(value.substring(lastIndex));
      // Preserve trailing newline so backdrop height matches
      if (result.endsWith('\n')) result += ' ';
      return result;
    }, [value, allMentions, trigger]);

    return (
      <div className="relative">
        {/* Highlight backdrop */}
        <div
          ref={backdropRef}
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-sm text-transparent',
            '[&_.mention-highlight]:rounded [&_.mention-highlight]:bg-blue-100 [&_.mention-highlight]:text-transparent',
            className,
          )}
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />

        {/* Actual textarea */}
        <textarea
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onScroll={syncScroll}
          disabled={disabled}
          className={cn(
            'relative flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...props}
        />

        {/* Mention dropdown */}
        {menuOpen && filtered.length > 0 && (
          <div
            ref={popoverRef}
            className="fixed z-[9999] max-h-[200px] min-w-[180px] overflow-y-auto rounded-md border bg-white p-1 shadow-md"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {filtered.map((option, idx) => (
              <div
                key={option.value}
                className={cn(
                  'flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm',
                  idx === highlightedIndex && 'bg-accent text-accent-foreground',
                )}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault(); // keep textarea focus
                  insertMention(option);
                }}
              >
                {option.value === 'feedback' ? (
                  <span className="font-medium text-blue-600">{trigger}feedback</span>
                ) : (
                  <span>{option.label}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);
MentionTextarea.displayName = 'MentionTextarea';

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;',
};

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch]!);
}

const MIRROR_STYLE_PROPS = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
  'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing',
  'textIndent', 'paddingTop', 'paddingRight', 'paddingBottom',
  'paddingLeft', 'borderTopWidth', 'borderRightWidth',
  'borderBottomWidth', 'borderLeftWidth', 'boxSizing',
  'whiteSpace', 'wordWrap', 'overflowWrap',
] as const;

function measureCaretPosition(textarea: HTMLTextAreaElement): { top: number; left: number } {
  const mirror = document.createElement('div');
  const style = window.getComputedStyle(textarea);

  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.overflow = 'hidden';
  mirror.style.width = `${textarea.offsetWidth}px`;
  mirror.style.height = 'auto';
  for (const p of MIRROR_STYLE_PROPS) {
    mirror.style[p as string] = style[p as string];
  }

  mirror.textContent = textarea.value.substring(0, textarea.selectionStart ?? 0);

  const span = document.createElement('span');
  span.textContent = '\u200b'; // zero-width space
  mirror.appendChild(span);

  document.body.appendChild(mirror);
  const taRect = textarea.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  document.body.removeChild(mirror);

  return {
    top: spanRect.top - mirrorRect.top - textarea.scrollTop + taRect.top,
    left: spanRect.left - mirrorRect.left - textarea.scrollLeft + taRect.left,
  };
}

export { MentionTextarea };
